import { Box, Button, Paper, Stack, Typography, alpha, useTheme, Divider, Skeleton } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { LuArrowLeft, LuFileJson, LuBox, LuFolders, LuBuilding, LuLayoutTemplate } from "react-icons/lu";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../authentication/AuthProvider";
import { Company } from "../../db_access/company";
import { PublishedTemplate } from "../../db_access/published_template";
import routing from "../../routing";
import CompanyPrebuiltTemplateGrid from "./CompanyPreBuiltTemplateGrid";
import PublishedTemplateGrid from "./PreBuiltTemplateGrid";
import TemplateDashboard from "./TemplateDashboard";
import MetricCard from "../../app_components/MetricCard";
import { getOrgTokens, ORG_PALETTE } from "../../theme/style/organizationPalette";

const PrebuiltTemplateManagement = () => {
    const { t } = useTranslation();
    const { isAdmin } = useAuth();
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const tok = getOrgTokens(isDark);
    const navigate = useNavigate();

    const [companyOptions, setCompanyOptions] = useState([]);
    const [selectedCompanyID, setSelectedCompanyID] = useState(null);
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);

    // View State: 'dashboard' or specific category key
    const [currentView, setCurrentView] = useState('dashboard');

    useEffect(() => {
        if (!isAdmin) {
            navigate(routing.home);
            return;
        }

        setLoading(true);
        Promise.all([
            Company.get_all().then(res => res.json()),
            PublishedTemplate.get_all().then(res => res.json())
        ]).then(([companies, templatesData]) => {
            setCompanyOptions(companies.map((c) => ({ label: c.name, value: c.id })));
            setTemplates(templatesData);
            setLoading(false);
        }).catch(err => {
            console.error("Error fetching template management data:", err);
            setLoading(false);
        });
    }, [isAdmin, navigate]);

    const viewTitles = {
        product: 'Product Templates',
        project: 'Project Templates',
        building: 'Building Templates',
        structure: 'Organization Templates'
    };

    const categoryStats = useMemo(() => {
        const stats = { product: 0, project: 0, building: 0, structure: 0 };
        templates.forEach(t => {
            if (stats[t.type] !== undefined) stats[t.type]++;
        });
        return stats;
    }, [templates]);

    const topMetrics = useMemo(() => [
        { id: 'product', title: t("Product"), value: categoryStats.product, unit: t("templates"), icon: LuBox, type: 'chain', accent: '#3b82f6', bg: 'rgba(59, 130, 246, 0.1)' },
        { id: 'project', title: t("Project"), value: categoryStats.project, unit: t("templates"), icon: LuFolders, type: 'scope3', accent: '#10b981', bg: 'rgba(16, 185, 129, 0.1)' },
        { id: 'building', title: t("Building"), value: categoryStats.building, unit: t("templates"), icon: LuBuilding, type: 'scope2', accent: '#f59e0b', bg: 'rgba(245, 158, 11, 0.1)' },
        { id: 'structure', title: t("Organization"), value: categoryStats.structure, unit: t("templates"), icon: LuLayoutTemplate, type: 'shield', accent: '#8b5cf6', bg: 'rgba(139, 92, 246, 0.1)' }
    ], [categoryStats, t]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: tok.spacing.pageGap, animation: 'fadeIn 0.6s ease-out' }}>

            {/* 1. Standardized Header */}
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                pb: tok.sectionHeading.titlePb,
                borderBottom: tok.sectionHeading.borderBottom
            }}>
                <Stack direction="row" spacing={2} alignItems="center">
                    {currentView !== 'dashboard' && (
                        <Button
                            onClick={() => setCurrentView('dashboard')}
                            sx={{
                                minWidth: 40,
                                height: 40,
                                borderRadius: tok.radius.button,
                                borderColor: tok.border.card,
                                color: tok.text.primary,
                                '&:hover': { bgcolor: tok.bg.tabActive, borderColor: tok.bg.tabActive, color: '#fff' }
                            }}
                            variant="outlined"
                        >
                            <LuArrowLeft size={20} />
                        </Button>
                    )}
                    <Typography sx={{
                        ...tok.sectionHeading,
                        borderLeft: `${tok.sectionHeading.barWidth} solid ${tok.sectionHeading.barColor}`,
                        pl: 1,
                        borderBottom: 'none',
                        textAlign: 'left'
                    }}>
                        {currentView === 'dashboard' ? t("Template Management") : t(viewTitles[currentView])}
                    </Typography>
                </Stack>
            </Box>

            {/* 2. Metrics Row - Only visible in Dashboard View */}
            {currentView === 'dashboard' && (
                <Box sx={{ display: 'flex', gap: tok.spacing.metricCardGap, flexWrap: 'wrap', width: '100%', mb: 1 }}>
                    {loading ? (
                        Array.from(new Array(4)).map((_, i) => (
                            <Box key={i} sx={{ flex: '1 1 0', minWidth: '240px' }}>
                                <Paper sx={{ p: 3, height: '150px', borderRadius: tok.radius.card, border: '1px solid', borderColor: tok.border.card, bgcolor: tok.bg.card }}>
                                    <Skeleton variant="circular" width={44} height={44} sx={{ mb: 2 }} />
                                    <Skeleton width="60%" height={32} />
                                </Paper>
                            </Box>
                        ))
                    ) : (
                        topMetrics.map((m, i) => (
                            <Box key={m.id} sx={{ flex: '1 1 0', minWidth: '240px' }}>
                                <MetricCard
                                    title={m.title}
                                    value={m.value}
                                    unit={m.unit}
                                    icon={m.icon}
                                    type={m.type}
                                    bg={m.bg}
                                    delay={i * 0.1}
                                />
                            </Box>
                        ))
                    )}
                </Box>
            )}

            {/* 3. Main Content Area */}
            <Box sx={{ mt: 1 }}>
                {currentView !== 'dashboard' && (
                    <Box sx={{
                        ...tok.sectionHeading,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        pl: 0, // Reset padding left for the bar
                        position: 'relative',
                        mb: 3
                    }}>
                        <Box sx={{
                            width: '4px',
                            height: '24px',
                            bgcolor: tok.sectionHeading.barColor,
                            borderRadius: '2px'
                        }} />
                        <Typography sx={{
                            fontFamily: tok.sectionHeading.fontFamily,
                            fontSize: tok.sectionHeading.fontSize,
                            fontWeight: tok.sectionHeading.fontWeight,
                            color: tok.sectionHeading.color
                        }}>
                            {t(`${currentView.charAt(0).toUpperCase() + currentView.slice(1)} Templates`)}
                        </Typography>
                    </Box>
                )}
                {currentView === 'dashboard' ? (
                    <TemplateDashboard onSelectCategory={setCurrentView} />
                ) : (
                    <Box sx={{ animation: 'fadeIn 0.4s ease' }}>
                        <Box sx={{ mb: 4 }}>
                            <PublishedTemplateGrid selectedCompanyID={selectedCompanyID} type={currentView} />
                        </Box>

                        <Divider sx={{ my: 4, opacity: 0.5 }} />

                        <Box sx={{ mb: 4 }}>
                            <Box sx={{
                                ...tok.sectionHeading,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                pl: 0,
                                position: 'relative',
                                mb: 3
                            }}>
                                <Box sx={{
                                    width: '4px',
                                    height: '24px',
                                    bgcolor: tok.sectionHeading.barColor,
                                    borderRadius: '2px'
                                }} />
                                <Typography sx={{
                                    fontFamily: tok.sectionHeading.fontFamily,
                                    fontSize: tok.sectionHeading.fontSize,
                                    fontWeight: tok.sectionHeading.fontWeight,
                                    color: tok.sectionHeading.color
                                }}>
                                    {t("Organization Specific Templates")}
                                </Typography>
                            </Box>

                            {/* Standardized Organization Selector - Compact High Density Grid */}
                            <Box sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)', lg: 'repeat(6, 1fr)' },
                                gap: 2,
                                mb: 4
                            }}>
                                {companyOptions.map((company) => {
                                    const isSelected = selectedCompanyID === company.value;
                                    return (
                                        <Box
                                            key={company.value}
                                            onClick={() => setSelectedCompanyID(company.value)}
                                            sx={{
                                                p: 1.5,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1.5,
                                                cursor: 'pointer',
                                                borderRadius: '10px',
                                                border: '1px solid',
                                                borderColor: isSelected ? tok.bg.tabActive : tok.border.card,
                                                bgcolor: isSelected ? alpha(tok.bg.tabActive, 0.04) : tok.bg.card,
                                                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                                '&:hover': {
                                                    borderColor: tok.bg.tabActive,
                                                    transform: 'translateY(-1px)',
                                                    bgcolor: alpha(tok.bg.tabActive, 0.02)
                                                }
                                            }}
                                        >
                                            <Box sx={{
                                                width: 32, height: 32,
                                                borderRadius: '6px',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                bgcolor: isSelected ? tok.bg.tabActive : alpha(tok.bg.tabActive, 0.1),
                                                color: isSelected ? '#fff' : tok.bg.tabActive,
                                                fontWeight: 800,
                                                fontSize: '14px',
                                                flexShrink: 0
                                            }}>
                                                {company.label.charAt(0).toUpperCase()}
                                            </Box>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography sx={{
                                                    fontSize: '13.5px',
                                                    fontWeight: 600,
                                                    color: isSelected ? tok.bg.tabActive : tok.text.primary,
                                                    lineHeight: 1.1,
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis'
                                                }}>
                                                    {company.label}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Box>

                            {selectedCompanyID ? (
                                <CompanyPrebuiltTemplateGrid selectedCompanyID={selectedCompanyID} type={currentView} />
                            ) : (
                                <Paper sx={{
                                    p: 6, textAlign: 'center',
                                    borderRadius: tok.radius.card,
                                    border: '2px dashed',
                                    borderColor: tok.border.divider,
                                    bgcolor: alpha(tok.bg.card, 0.5)
                                }}>
                                    <Typography sx={{ ...tok.gridCellText, color: tok.text.muted }}>
                                        {t("Select an organization above to view its specific templates")}
                                    </Typography>
                                </Paper>
                            )}
                        </Box>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default PrebuiltTemplateManagement;
